import styled from 'styled-components'
import { useTranslation, Trans } from 'react-i18next'
import { isNil, length } from 'ramda'
import { colors } from '@/styles'
import { EquifaxLogomark } from '@/components/icons'
import { ValueText, LabeledValue, LabelText } from '@/features/Application/elements'
import { Box } from '@machobear/design-system'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  > :not(:last-child) {
    border-bottom: 1px solid ${colors.grey88};
  }
  > * {
    padding: 12px;
  }
`

const Summary = ({ data, isLocked, application, isLoading, standalone }) => {
  const { t } = useTranslation()

  const courtRecordTexts = {
    records_found: t('l.application.view.found.label', 'Found'),
    records_not_found: t('l.application.view.not_found.label', 'Not found'),
    analyzing: t('l.application.view.analyzing_singular.text', 'Analyzing')
  }
  const unabletoRetrieveText = t('l.application.view.unable_to_retrieve.text', 'Unable to retrieve')
  const isActive =
    application?.trust_score?.status === 'active' ||
    (standalone && application?.status !== 'failed')
  const bankruptciesCount = data?.bankruptcy?.details?.length
  const hasBankruptcyWarning = bankruptciesCount > 0
  const collectionsCount = data?.collection?.details?.length
  const hasCollectionsWarning = collectionsCount > 0

  return (
    <Wrapper>
      <LabeledValue
        label={
          <LabelText>{t('l.application.view.court_records.label', 'Court Records')}</LabelText>
        }
        value={
          <ValueText
            hasWarning={data?.court_verification === 'records_found'}
            hasSuccess={data?.court_verification === 'records_not_found'}
            isLocked={isLocked}
          >
            {courtRecordTexts[data?.court_verification] ?? '-'}
          </ValueText>
        }
        isLoading={isLoading}
      />
      <LabeledValue
        label={
          <Box display="flex" alignItems="center" gap="xxsmall">
            <LabelText>
              {t('l.application.view.potential_risks.label', 'Potential Risks')}
            </LabelText>
            <EquifaxLogomark />
          </Box>
        }
        value={
          <ValueText
            isLocked={isLocked}
            hasWarning={length(data?.potential_risks?.details) > 0 || !isActive}
          >
            {isActive ? length(data?.potential_risks?.details) ?? '-' : unabletoRetrieveText}
          </ValueText>
        }
        isLoading={isLoading}
      />
      <LabeledValue
        label={
          <Box display="flex" alignItems="center" gap="xxsmall">
            <LabelText>{t('l.application.view.bankruptcies.label', 'Bankruptcies')}</LabelText>{' '}
            <EquifaxLogomark />
          </Box>
        }
        value={
          <ValueText hasWarning={hasBankruptcyWarning || !isActive} isLocked={isLocked}>
            {isActive ? (
              isNil(bankruptciesCount) ? (
                '-'
              ) : (
                <Trans
                  i18nKey="l.application.view.bankruptcies_count.label"
                  t={t}
                  bankruptciesCount={bankruptciesCount}
                >
                  {{ bankruptciesCount }} Records
                </Trans>
              )
            ) : (
              unabletoRetrieveText
            )}
          </ValueText>
        }
        isLoading={isLoading}
      />
      <LabeledValue
        label={
          <Box display="flex" alignItems="center" gap="xxsmall">
            <LabelText>{t('l.application.view.collections.label', 'Collections')}</LabelText>{' '}
            <EquifaxLogomark />
          </Box>
        }
        value={
          <ValueText hasWarning={hasCollectionsWarning || !isActive} isLocked={isLocked}>
            {isActive ? (
              isNil(collectionsCount) ? (
                '-'
              ) : (
                <Trans
                  i18nKey="l.application.view.collections_count.label"
                  t={t}
                  collectionsCount={collectionsCount}
                >
                  {{ collectionsCount }} Records
                </Trans>
              )
            ) : (
              unabletoRetrieveText
            )}
          </ValueText>
        }
        isLoading={isLoading}
      />
    </Wrapper>
  )
}

export default Summary
